import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    usePlotArea
} from "recharts"

import {
    buildEmployeeMonthChartSeries,
    buildGapSegments,
    monthHasThirdShiftLogs
} from "../../../utils/employeeMonthLogsChartSeries"

export const NARROW_CHART_WIDTH_PX = 574

const Y_TICKS = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24]

const CHART_MARGIN = { top: 12, right: 9, left: 34, bottom: 24 }
const CHART_MARGIN_NARROW = { top: 4, right: 2, left: 26, bottom: 12 }

const formatClockFromY = (y, yAxisOffsetHours) => {
    const total = yAxisOffsetHours + y
    const h = Math.floor(total) % 24
    return `${String(h).padStart(2, "0")}:00`
}

const ChartDayClickOverlay = ({ data, dayMin, dayMax, year, monthIndex, onSelectDay }) => {
    const plot = usePlotArea()
    if (!plot?.width || !plot.height || !data.length || !onSelectDay) return null

    const span = Math.max(dayMax - dayMin, 1)
    const colW = plot.width / span

    return (
        <g className="asv-employee-chart-day-hit" aria-hidden>
            {data.map((row) => {
                const x = plot.x + ((row.day - dayMin) / span) * plot.width - colW / 2
                return (
                    <rect
                        key={row.day}
                        x={x}
                        y={plot.y}
                        width={colW}
                        height={plot.height}
                        fill="transparent"
                        style={{ cursor: "pointer" }}
                        onClick={(ev) => {
                            ev.stopPropagation()
                            onSelectDay({ year, month: monthIndex, date: row.day })
                        }}
                    />
                )
            })}
        </g>
    )
}

const ChartGapBridgesPlot = ({ gapSegsByKey, strokeBridge, dayMin, dayMax }) => {
    const plot = usePlotArea()
    if (!plot?.width || !plot.height) return null

    const span = Math.max(dayMax - dayMin, 1)
    const xAt = (day) => plot.x + ((day - dayMin) / span) * plot.width
    const yAt = (v) => plot.y + plot.height - (v / 24) * plot.height

    return (
        <g className="asv-employee-chart-gap-bridges" aria-hidden>
            {gapSegsByKey.flatMap(({ key, segs }) =>
                segs.map((seg, i) => (
                    <line
                        key={`${key}-${seg.dayA}-${seg.dayB}-${i}`}
                        x1={xAt(seg.dayA)}
                        y1={yAt(seg.yA)}
                        x2={xAt(seg.dayB)}
                        y2={yAt(seg.yB)}
                        stroke={strokeBridge}
                        strokeWidth={1.25}
                        fill="none"
                    />
                ))
            )}
        </g>
    )
}

const TOOLTIP_SERIES_KEYS = [
    { key: "arrivalY", labelKey: "chart.arrival" },
    { key: "exitY", labelKey: "chart.exit" },
    { key: "arrivalThirdY", labelKey: "chart.arrivalThird" },
    { key: "exitThirdY", labelKey: "chart.exitThird" }
]

const DayChartTooltip = ({ active, payload, label, isDarkTheme, yAxisOffsetHours }) => {
    const { t } = useTranslation("stats")
    if (!active || !payload?.length) return null

    const row = payload[0]?.payload
    if (!row) return null

    const formatTime = (y) => {
        if (y == null || Number.isNaN(y)) return null
        const totalMins = Math.round((yAxisOffsetHours + y) * 60)
        const h = Math.floor(totalMins / 60) % 24
        const m = totalMins % 60
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
    }

    const items = TOOLTIP_SERIES_KEYS.map(({ key, labelKey }) => {
        const y = row[key]
        const time = formatTime(y)
        if (time == null) return null
        return (
            <p key={key} style={{ margin: "2px 0", color: isDarkTheme ? "#e2e8f0" : "#334155" }}>
                {t(labelKey)}: {time}
            </p>
        )
    }).filter(Boolean)

    if (!items.length) return null

    return (
        <div
            style={{
                background: isDarkTheme ? "#0f172a" : "#fff",
                border: `1px solid ${isDarkTheme ? "#334155" : "#e2e8f0"}`,
                borderRadius: 8,
                fontSize: 13,
                padding: "8px 10px"
            }}
        >
            <p style={{ margin: "0 0 4px", fontWeight: 600, color: isDarkTheme ? "#f1f5f9" : "#0f172a" }}>
                {t("chart.dayTooltip", { day: label })}
            </p>
            {items}
        </div>
    )
}

const EmployeeMonthHoursLineChart = ({
    year,
    monthIndex,
    isDarkTheme,
    logsByDay,
    onSelectDay,
    isNarrow,
    chartHeight
}) => {
    const { t } = useTranslation("stats")
    const chartMargin = isNarrow ? CHART_MARGIN_NARROW : CHART_MARGIN
    const tickFontSize = isNarrow ? 10 : 14
    const useMidnightAxis = useMemo(() => monthHasThirdShiftLogs(logsByDay), [logsByDay])
    const yAxisOffsetHours = useMidnightAxis ? 0 : 5
    const data = useMemo(() => buildEmployeeMonthChartSeries(logsByDay, year, monthIndex), [logsByDay, year, monthIndex])
    const arrivalGapSegs = useMemo(() => buildGapSegments(data, "arrivalY"), [data])
    const exitGapSegs = useMemo(() => buildGapSegments(data, "exitY"), [data])
    const dayMin = useMemo(() => (data.length ? Math.min(...data.map((d) => d.day)) : 1), [data])
    const dayMax = useMemo(() => (data.length ? Math.max(...data.map((d) => d.day)) : 1), [data])
    const tickFill = isDarkTheme ? "#b8c4d4" : "#4a5f6f"
    const gridStroke = isDarkTheme ? "rgba(148, 163, 184, 0.35)" : "rgba(100, 116, 139, 0.28)"
    const strokeArrival = isDarkTheme ? "#2dd4bf" : "#0d9488"
    const strokeExit = isDarkTheme ? "#f472b6" : "#db2777"
    const strokeBridge = isDarkTheme ? "rgba(148, 163, 184, 0.55)" : "rgba(100, 116, 139, 0.45)"
    const dotRing = isDarkTheme ? "#0f172a" : "#fff"

    const dotIfValue = (fill) => (props) => {
        const { cx, cy, value } = props
        if (value == null || Number.isNaN(value)) return null
        return (
            <circle
                cx={cx}
                cy={cy}
                r={4}
                fill={fill}
                stroke={dotRing}
                strokeWidth={1.5}
                style={{ pointerEvents: "none" }}
            />
        )
    }

    return (
        <div className={`asv-employee-chart-panel ${isDarkTheme ? "asv-employee-chart-panel--dark" : ""}`}>
            <div className="asv-employee-chart-panel-chart">
                <ResponsiveContainer
                    width="100%"
                    height={chartHeight ?? "100%"}
                    minWidth={0}
                    initialDimension={{ width: 640, height: chartHeight ?? 280 }}
                >
                    <LineChart data={data} margin={chartMargin}>
                        <CartesianGrid stroke={gridStroke} strokeDasharray="0" vertical horizontal />
                        <XAxis
                            dataKey="day"
                            type="number"
                            domain={["dataMin", "dataMax"]}
                            tick={{ fill: tickFill, fontSize: tickFontSize }}
                            tickCount={Math.min(data.length, 16)}
                            label={{
                                value: t("chart.day"),
                                position: "insideBottom",
                                offset: -4,
                                fill: tickFill,
                                fontSize: isNarrow ? 10 : 15
                            }}
                        />
                        <YAxis
                            domain={[0, 24]}
                            type="number"
                            ticks={Y_TICKS}
                            tickFormatter={(y) => formatClockFromY(y, yAxisOffsetHours)}
                            tick={{ fill: tickFill, fontSize: tickFontSize }}
                            width={chartMargin.left}
                            interval={0}
                        />
                        <Tooltip
                            content={
                                <DayChartTooltip isDarkTheme={isDarkTheme} yAxisOffsetHours={yAxisOffsetHours} />
                            }
                        />
                        {!useMidnightAxis ? (
                            <ChartGapBridgesPlot
                                gapSegsByKey={[
                                    { key: "ar", segs: arrivalGapSegs },
                                    { key: "ex", segs: exitGapSegs }
                                ]}
                                strokeBridge={strokeBridge}
                                dayMin={dayMin}
                                dayMax={dayMax}
                            />
                        ) : null}
                        <Line
                            type="monotone"
                            dataKey="arrivalY"
                            name="arrivalY"
                            stroke={strokeArrival}
                            strokeWidth={2}
                            dot={dotIfValue(strokeArrival)}
                            activeDot={false}
                            connectNulls={false}
                            isAnimationActive={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="exitY"
                            name="exitY"
                            stroke={strokeExit}
                            strokeWidth={2}
                            dot={dotIfValue(strokeExit)}
                            activeDot={false}
                            connectNulls={false}
                            isAnimationActive={false}
                        />
                        {useMidnightAxis ? (
                            <>
                                <Line
                                    type="monotone"
                                    dataKey="arrivalThirdY"
                                    name="arrivalThirdY"
                                    stroke={strokeArrival}
                                    strokeWidth={2}
                                    strokeDasharray="5 4"
                                    dot={dotIfValue(strokeArrival)}
                                    activeDot={false}
                                    connectNulls={false}
                                    isAnimationActive={false}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="exitThirdY"
                                    name="exitThirdY"
                                    stroke={strokeExit}
                                    strokeWidth={2}
                                    strokeDasharray="5 4"
                                    dot={dotIfValue(strokeExit)}
                                    activeDot={false}
                                    connectNulls={false}
                                    isAnimationActive={false}
                                />
                            </>
                        ) : null}
                        <ChartDayClickOverlay
                            data={data}
                            dayMin={dayMin}
                            dayMax={dayMax}
                            year={year}
                            monthIndex={monthIndex}
                            onSelectDay={onSelectDay}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div className={`asv-employee-chart-legend ${isDarkTheme ? "asv-employee-chart-legend--dark" : ""}`}>
                <span className="asv-employee-chart-legend-item">
                    <span className="asv-employee-chart-legend-line asv-employee-chart-legend-line--arrival" style={{ background: strokeArrival }} />
                    {t("chart.arrival")}
                </span>
                <span className="asv-employee-chart-legend-item">
                    <span className="asv-employee-chart-legend-line asv-employee-chart-legend-line--exit" style={{ background: strokeExit }} />
                    {t("chart.exit")}
                </span>
                {useMidnightAxis ? (
                    <>
                        <span className="asv-employee-chart-legend-item">
                            <span
                                className="asv-employee-chart-legend-line asv-employee-chart-legend-line--scheduled"
                                style={{ borderColor: strokeArrival }}
                            />
                            {t("chart.arrivalThird")}
                        </span>
                        <span className="asv-employee-chart-legend-item">
                            <span
                                className="asv-employee-chart-legend-line asv-employee-chart-legend-line--scheduled"
                                style={{ borderColor: strokeExit }}
                            />
                            {t("chart.exitThird")}
                        </span>
                    </>
                ) : null}
            </div>
        </div>
    )
}

export default EmployeeMonthHoursLineChart
